import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Cartao, RolagemHorizontal } from '../../componentes/base';
import { Estado } from '../../componentes/estados';
import { Formulario, Selecao, type DefCampo } from '../../componentes/formulario';
import { Confirmar, Modal } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { api, del, get, mensagemDeErro, patch, post, getTodos } from '../../nucleo/api';
import { useSessao } from '../../nucleo/sessao';
import { Nivel } from './FichaPartes';
import { limpar, type Habilidade, type Matriz } from './tipos';
import { TextoCortado } from '../../componentes/texto-cortado';
import { nomePessoa } from '../../nucleo/formato';

interface Celula {
  funcionarioId: string;
  funcionarioNome: string;
  habilidadeId: string;
  habilidadeNome: string;
  nivel: number;
}

/** M7.5: catálogo de habilidades e matriz pessoa x habilidade. */
export function Habilidades() {
  const { t } = useTranslation('pessoas');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [aberto, setAberto] = useState<Habilidade | 'nova' | null>(null);
  const [remover, setRemover] = useState<Habilidade | null>(null);
  const [celula, setCelula] = useState<Celula | null>(null);
  const [nivel, setNivel] = useState('0');
  const catalogo = useQuery({ queryKey: ['pessoas', 'habilidades'], queryFn: () => getTodos<Habilidade>('/pessoas/habilidades') });
  const matriz = useQuery({ queryKey: ['pessoas', 'matriz'], queryFn: () => get<Matriz>('/pessoas/habilidades/matriz') });
  const atual = aberto && aberto !== 'nova' ? aberto : null;
  const recarregar = () => qc.invalidateQueries({ queryKey: ['pessoas'] });
  const podeEditar = s.pode('pessoas.editar');
  // Val5 (29/10): o catálogo de habilidades é estrutura (`pessoas.configurar`); o nível de cada pessoa segue `pessoas.editar`.
  const podeConfigurar = s.pode('pessoas.configurar');

  const excluir = useMutation({
    mutationFn: (id: string) => del(`/pessoas/habilidades/${id}`),
    onSuccess: async () => {
      setRemover(null);
      await recarregar();
    },
    onError: (e) => {
      setRemover(null);
      toast.erro(mensagemDeErro(e));
    },
  });

  const definir = useMutation({
    mutationFn: async (v: { c: Celula; nivel: number }) => {
      const caminho = `/pessoas/funcionarios/${v.c.funcionarioId}/habilidades/${v.c.habilidadeId}`;
      if (v.nivel === 0 && v.c.nivel === 0) return;
      if (v.nivel === 0) await del(caminho);
      else await api('PUT', caminho, { nivel: v.nivel });
    },
    onSuccess: async () => {
      setCelula(null);
      await recarregar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });

  const campos: DefCampo[] = [
    { nome: 'nome', rotulo: t('campos.nome'), tipo: 'texto', obrigatorio: true, largura: 'meia' },
    { nome: 'categoria', rotulo: t('campos.categoria'), tipo: 'texto', largura: 'meia' },
  ];

  return (
    <>
      {podeConfigurar && (
        <AcaoPrimaria>
          <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setAberto('nova')}>
            {t('acoes.novaHabilidade')}
          </Botao>
        </AcaoPrimaria>
      )}
      <div className="grid min-w-0 gap-4 lg:grid-cols-3">
        <Cartao titulo={t('habilidades.catalogo')} className="h-fit">
          <Estado consulta={catalogo} vazio={(d) => d.itens.length === 0}>
            {(d) => (
              <ul className="divide-y divide-borda">
                {d.itens.map((h) => (
                  <li key={h.id} className="flex min-w-0 items-center justify-between gap-3 py-2 text-sm">
                    <span className="flex min-w-0 flex-col">
                      <span className="truncate font-medium" title={h.nome}>{h.nome}</span>
                      <TextoCortado className="text-xs text-tinta-3">{[h.categoria, t('habilidades.pessoas', { count: h.funcionarios })].filter(Boolean).join(' / ')}</TextoCortado>
                    </span>
                    <span className="flex items-center gap-1">
                      {podeConfigurar && (
                        <button type="button" aria-label={tn('comum.editar')} onClick={() => setAberto(h)} className="rounded p-1 text-tinta-3 hover:text-tinta-1">
                          <Pencil className="size-4" />
                        </button>
                      )}
                      {podeConfigurar && (
                        <button type="button" aria-label={tn('comum.excluir')} onClick={() => setRemover(h)} className="rounded p-1 text-tinta-3 hover:text-perigo">
                          <Trash2 className="size-4" />
                        </button>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Estado>
        </Cartao>

        <Cartao titulo={t('habilidades.matriz')} className="lg:col-span-2" semPadding>
          <Estado consulta={matriz} vazio={(d) => d.habilidades.length === 0 || d.funcionarios.length === 0}>
            {(d) => (
              <RolagemHorizontal>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-borda text-left">
                      <th scope="col" className="px-4 py-2.5 font-medium whitespace-nowrap text-tinta-3">
                        {t('campos.funcionario')}
                      </th>
                      {d.habilidades.map((h) => (
                        <th key={h.id} scope="col" className="px-3 py-2.5 text-center font-medium whitespace-nowrap text-tinta-3">
                          {h.nome}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {d.funcionarios.map((f) => (
                      <tr key={f.id} className="border-b border-borda last:border-0">
                        <th scope="row" className="px-4 py-2 text-left font-normal whitespace-nowrap">
                          <Link to={`/pessoas/pessoas/funcionario/${f.id}`} className="hover:text-acento-forte">
                            {nomePessoa(f.nome)}
                          </Link>
                          {f.funcao_nome && <span className="block text-xs text-tinta-3">{f.funcao_nome}</span>}
                        </th>
                        {d.habilidades.map((h) => {
                          const n = f.niveis[h.id] ?? 0;
                          return (
                            <td key={h.id} className="px-3 py-2 text-center">
                              <button
                                type="button"
                                disabled={!podeEditar}
                                aria-label={`${nomePessoa(f.nome)}, ${h.nome}: ${n || t('habilidades.semNivel')}`}
                                onClick={() => {
                                  setCelula({ funcionarioId: f.id, funcionarioNome: f.nome, habilidadeId: h.id, habilidadeNome: h.nome, nivel: n });
                                  setNivel(String(n));
                                }}
                                className="inline-flex min-h-6 min-w-12 items-center justify-center rounded enabled:hover:bg-sup-2"
                              >
                                {n ? <Nivel n={n} /> : <span className="text-tinta-3">-</span>}
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </RolagemHorizontal>
            )}
          </Estado>
        </Cartao>
      </div>

      <Modal
        aberto={!!aberto}
        aoFechar={() => setAberto(null)}
        titulo={atual ? atual.nome : t('acoes.novaHabilidade')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={() => setAberto(null)}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao variante="primario" type="submit" form="form-habilidade">
              {tn('comum.salvar')}
            </Botao>
          </>
        }
      >
        <Formulario
          key={atual?.id ?? 'nova'}
          id="form-habilidade"
          campos={campos}
          inicial={(atual ?? {}) as unknown as Record<string, unknown>}
          aoEnviar={async (v) => {
            const corpo = limpar({ nome: v.nome, categoria: v.categoria });
            if (atual) await patch(`/pessoas/habilidades/${atual.id}`, corpo);
            else await post('/pessoas/habilidades', corpo);
            await recarregar();
            toast.sucesso(tn('comum.salvo'));
            setAberto(null);
          }}
        />
      </Modal>

      <Modal
        aberto={!!celula}
        aoFechar={() => setCelula(null)}
        titulo={celula ? `${celula.funcionarioNome}: ${celula.habilidadeNome}` : ''}
        rodape={
          <>
            <Botao variante="fantasma" onClick={() => setCelula(null)}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao variante="primario" carregando={definir.isPending} onClick={() => celula && definir.mutate({ c: celula, nivel: Number(nivel) })}>
              {tn('comum.salvar')}
            </Botao>
          </>
        }
      >
        <label className="flex flex-col gap-1.5 text-sm font-medium text-tinta-2">
          {t('campos.nivel')}
          <Selecao opcoes={[0, 1, 2, 3, 4, 5].map((n) => ({ valor: String(n), rotulo: n === 0 ? t('habilidades.semNivel') : `${n} - ${t(`nivel.${n}`)}` }))} value={nivel} onChange={(e) => setNivel(e.target.value)} />
        </label>
      </Modal>

      <Confirmar aberto={!!remover} aoFechar={() => setRemover(null)} aoConfirmar={() => remover && excluir.mutate(remover.id)} titulo={t('confirmar.excluirHabilidade')} texto={t('confirmar.excluirHabilidadeTexto')} perigoso carregando={excluir.isPending} />
    </>
  );
}
